import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, Menu, X, Search, Heart } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/981795dc-3f85-41a2-8218-f3db7d35fb62/images/33e54ddee2de433632b5f87b4e137bb11cce4f32e0d5989a55474dd8cf991f88.png";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { count: wishlistCount } = useWishlist();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#001F3F]/80 border-b border-white/10"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="Nord Stella Tech"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-xs font-bold uppercase tracking-[0.25em] text-white/80">
          <Link to="/shop" className="hover:text-white transition" data-testid="nav-shop">
            {t("nav.shop")}
          </Link>
          <Link
            to="/shop?category=smart"
            className="hover:text-white transition"
            data-testid="nav-smart"
          >
            Smart Tech
          </Link>
          <Link
            to="/shop?category=wellness"
            className="hover:text-white transition"
            data-testid="nav-wellness"
          >
            Wellness
          </Link>
          <Link
            to="/about"
            className="hover:text-white transition"
            data-testid="nav-about"
          >
            {t("nav.about")}
          </Link>
          <Link
            to="/contact"
            className="hover:text-white transition"
            data-testid="nav-contact"
          >
            {t("nav.contact")}
          </Link>
        </nav>

        <div className="flex items-center gap-2 md:gap-4 text-white/80">
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>

          <button
            onClick={() => navigate("/shop")}
            className="hidden sm:block p-2 hover:text-white"
            aria-label="Search"
            data-testid="search-button"
          >
            <Search size={18} strokeWidth={1.5} />
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-2 hover:text-white hidden sm:flex items-center gap-2"
                  data-testid="user-menu-button"
                >
                  <User size={18} strokeWidth={1.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border-white/20 bg-[#0A2A4F] text-white w-56">
                <div className="px-3 py-2 text-xs uppercase tracking-widest text-white/60">
                  {user.email}
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  onClick={() => navigate("/account")}
                  data-testid="menu-account"
                  className="rounded-none cursor-pointer focus:bg-white/10"
                >
                  {t("nav.account")}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem
                    onClick={() => navigate("/admin")}
                    data-testid="menu-admin"
                    className="rounded-none cursor-pointer focus:bg-white/10"
                  >
                    {t("nav.admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  data-testid="menu-logout"
                  className="rounded-none cursor-pointer focus:bg-white/10"
                >
                  <LogOut size={14} className="mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              className="hidden sm:block text-xs font-bold uppercase tracking-[0.2em] hover:text-white"
              data-testid="login-link"
            >
              {t("nav.login")}
            </Link>
          )}

          <Link
            to="/wishlist"
            className="relative p-2 hover:text-white"
            data-testid="wishlist-link"
            aria-label={t("nav.wishlist")}
          >
            <Heart size={18} strokeWidth={1.5} />
            {wishlistCount > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-[#C5CDD8] text-[#001F3F] text-[10px] font-bold w-4 h-4 flex items-center justify-center"
                data-testid="wishlist-count"
              >
                {wishlistCount}
              </span>
            )}
          </Link>

          <Link
            to="/cart"
            className="relative p-2 hover:text-white"
            data-testid="cart-link"
          >
            <ShoppingBag size={18} strokeWidth={1.5} />
            {count > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-[#C5CDD8] text-[#001F3F] text-[10px] font-bold w-4 h-4 flex items-center justify-center"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </Link>

          <button
            onClick={() => setOpen(!open)}
            className="md:hidden p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#001F3F] px-6 py-4 space-y-3">
          <Link to="/shop" onClick={() => setOpen(false)} className="block text-sm font-bold uppercase tracking-widest text-white/80" data-testid="mobile-nav-shop">{t("nav.shop")}</Link>
          <Link to="/shop?category=smart" onClick={() => setOpen(false)} className="block text-sm font-bold uppercase tracking-widest text-white/80">Smart Tech</Link>
          <Link to="/shop?category=wellness" onClick={() => setOpen(false)} className="block text-sm font-bold uppercase tracking-widest text-white/80">Wellness</Link>
          <Link to="/about" onClick={() => setOpen(false)} className="block text-sm font-bold uppercase tracking-widest text-white/80">{t("nav.about")}</Link>
          <Link to="/contact" onClick={() => setOpen(false)} className="block text-sm font-bold uppercase tracking-widest text-white/80">{t("nav.contact")}</Link>
          <Link to="/wishlist" onClick={() => setOpen(false)} className="block text-sm font-bold uppercase tracking-widest text-white/80">{t("nav.wishlist")}{wishlistCount > 0 ? ` (${wishlistCount})` : ""}</Link>
          {!user && (
            <Link to="/login" onClick={() => setOpen(false)} className="block text-sm font-bold uppercase tracking-widest text-white/80">{t("nav.login")}</Link>
          )}
          {user && (
            <>
              <Link to="/account" onClick={() => setOpen(false)} className="block text-sm font-bold uppercase tracking-widest text-white/80">{t("nav.account")}</Link>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="block text-sm font-bold uppercase tracking-widest text-white/80">{t("nav.admin")}</Link>
              )}
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="block text-sm font-bold uppercase tracking-widest text-[#C5CDD8]">{t("nav.logout")}</button>
            </>
          )}
          <div className="pt-3 border-t border-white/10">
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </header>
  );
}
